import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useRecentEvents } from '../hooks/useRecentEvents';
import { useEventFilters } from '../hooks/useEventFilters';
import { useClearBuffer } from '../hooks/useClearBuffer';
import { EventTable } from '../components/EventTable';
import { EventFilterBar } from '../components/EventFilterBar';
import { EventDetailDrawer } from '../components/EventDetailDrawer';
import { ClearBufferConfirm } from '../components/ClearBufferConfirm';
import type { EventStreamItem, ConnectionMode, ApiError, RecentEventsCondition } from '../types/eventMonitor';
import { eventMonitorApi } from '../api/eventMonitorApi';

// R649-FE-001: 지수 백오프 1s→2s→4s→8s→16s · 최대 5회 시도 후 자동 재연결 중단
const SSE_RECONNECT_DELAY_BASE = 1000;
const SSE_RECONNECT_MAX_DELAY = 16000;
const SSE_MAX_RECONNECT_ATTEMPTS = 5;
// R649-FE-002(2차): 연결이 이 시간(ms) 이상 유지된 뒤에만 재연결 카운터를 리셋한다.
// 서버가 connected 직후 즉사(0초)를 반복하는 결함이 있을 때, 매 사이클마다 즉시 리셋되어
// attempt 가 항상 1에 머무는(=무한 재시도) 결함을 방지한다.
const STABLE_CONNECTION_MS = 60_000;
const POLLING_INTERVAL = 5000;
const MAX_DISPLAY_EVENTS = 1000;
/** Threshold in ms after which a live connection is considered "idle" (no event received) */
const LIVE_IDLE_THRESHOLD_MS = 60_000;

/** Buffer fill color based on usage ratio */
function bufferFillColor(size: number, capacity: number): string {
  const ratio = capacity > 0 ? size / capacity : 0;
  if (ratio >= 0.85) return 'var(--color-danger)';
  if (ratio >= 0.5) return 'var(--color-warning)';
  return 'var(--color-success)';
}

/** ConnectionMode badge styles */
function modeBadgeStyle(mode: ConnectionMode, idle: boolean): React.CSSProperties {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    padding: '2px var(--spacing-3)',
    borderRadius: 'var(--radius-full)',
    border: '1px solid',
    cursor: idle && mode === 'live' ? 'help' : 'default',
  };
  // R9-A-002: 'connecting' state — neutral/muted style (not green live)
  if (mode === 'connecting') {
    return {
      ...base,
      background: 'color-mix(in srgb, var(--color-text-muted) 10%, transparent)',
      color: 'var(--color-text-muted)',
      borderColor: 'var(--color-text-muted)',
    };
  }
  if (mode === 'live') {
    const color = idle ? 'var(--color-text-muted)' : 'var(--color-success)';
    const bg = idle
      ? 'color-mix(in srgb, var(--color-text-muted) 12%, transparent)'
      : 'color-mix(in srgb, var(--color-success) 15%, transparent)';
    return { ...base, background: bg, color, borderColor: color };
  }
  if (mode === 'paused') {
    return {
      ...base,
      background: 'color-mix(in srgb, var(--color-warning) 15%, transparent)',
      color: 'var(--color-warning)',
      borderColor: 'var(--color-warning)',
    };
  }
  if (mode === 'polling') {
    return {
      ...base,
      background: 'color-mix(in srgb, var(--color-info) 15%, transparent)',
      color: 'var(--color-info)',
      borderColor: 'var(--color-info)',
    };
  }
  return {
    ...base,
    background: 'color-mix(in srgb, var(--color-danger) 15%, transparent)',
    color: 'var(--color-danger)',
    borderColor: 'var(--color-danger)',
  };
}

/**
 * EventMonitorPage — ROLE_ADMIN dedicated real-time event stream monitor
 * Route: /event-monitor
 *
 * 5 states: default / loading / empty / error / dialog (drawer + confirm)
 */
export function EventMonitorPage() {
  const { t } = useTranslation();

  // --- filter state ---
  const [pendingTypes, setPendingTypes] = useState<string[]>([]);
  const [pendingSources, setPendingSources] = useState<string[]>([]);
  const [pendingUserId, setPendingUserId] = useState('');
  const [pendingSince, setPendingSince] = useState('');

  const [appliedCondition, setAppliedCondition] = useState<RecentEventsCondition>({ limit: 100 });

  // --- event list state ---
  const [events, setEvents] = useState<EventStreamItem[]>([]);
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());
  const [bufferSize, setBufferSize] = useState(0);
  const [bufferCapacity, setBufferCapacity] = useState(1000);

  // --- connection state ---
  // R9-A-002: start as 'connecting' — badge must not show green 'live' before SSE onopen/connected fires
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>('connecting');

  // --- idle-pill: track time of last received event ---
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const [isLiveIdle, setIsLiveIdle] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- pause/resume ---
  const [isPaused, setIsPaused] = useState(false);
  const pausedQueueRef = useRef<EventStreamItem[]>([]);
  const [pausedCount, setPausedCount] = useState(0);

  // --- selected event (drawer) ---
  const [selectedEvent, setSelectedEvent] = useState<EventStreamItem | null>(null);

  // --- dialog ---
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // --- toast notification ---
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'warning' } | null>(null);

  // --- SSE ---
  const sseRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // R649-FE-001: counts consecutive reconnect attempts (reset only after a STABLE_CONNECTION_MS
  // long-lived connection — see R649-FE-002 in `es.onerror`)
  const reconnectAttemptRef = useRef(0);
  // R649-FE-002(2차): timestamp of the most recent 'connected'/onopen — used by onerror to
  // decide whether the just-lost connection was "stable" long enough to reset the attempt budget.
  const connectedAtRef = useRef<number | null>(null);
  const pollingEnabledRef = useRef(false);
  const lastReceivedAtRef = useRef<string | undefined>(undefined);

  // --- TanStack Query ---
  const recentQuery = useRecentEvents(appliedCondition, {
    enabled: true,
    refetchInterval: pollingEnabledRef.current ? POLLING_INTERVAL : undefined,
  });

  const filtersQuery = useEventFilters();
  const clearMutation = useClearBuffer();

  // --- idle-pill interval: check every 5s if last event was >60s ago ---
  // R8-D-011: when lastEventAt === null (SSE not yet connected / no events seen),
  // do NOT mark as idle — the pill must start as '● 실시간' on first render.
  // Idle is only set true after SSE has been active and gone quiet for >60s.
  useEffect(() => {
    idleTimerRef.current = setInterval(() => {
      if (connectionMode !== 'live' && connectionMode !== 'connecting') {
        setIsLiveIdle(false);
        return;
      }
      if (connectionMode === 'connecting') {
        setIsLiveIdle(false);
        return;
      }
      if (lastEventAt === null) {
        // SSE not yet active — keep idle=false so badge shows live colour.
        setIsLiveIdle(false);
        return;
      }
      setIsLiveIdle(Date.now() - lastEventAt > LIVE_IDLE_THRESHOLD_MS);
    }, 5_000);
    return () => {
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, [connectionMode, lastEventAt]);

  // Also re-evaluate immediately when connectionMode or lastEventAt changes
  // R8-D-011: same guard — lastEventAt===null means not-yet-active, not idle.
  // R9-A-002: also guard 'connecting' state
  useEffect(() => {
    if (connectionMode !== 'live' && connectionMode !== 'connecting') {
      setIsLiveIdle(false);
      return;
    }
    if (connectionMode === 'connecting') {
      setIsLiveIdle(false);
      return;
    }
    if (lastEventAt === null) {
      // No events received yet — keep idle=false (initial live state).
      setIsLiveIdle(false);
      return;
    }
    setIsLiveIdle(Date.now() - lastEventAt > LIVE_IDLE_THRESHOLD_MS);
  }, [connectionMode, lastEventAt]);

  // --- initial data load → populate event list ---
  useEffect(() => {
    if (recentQuery.data) {
      const { items, bufferSize: bs, bufferCapacity: bc } = recentQuery.data;
      setEvents(items.slice(0, MAX_DISPLAY_EVENTS));
      setBufferSize(bs);
      setBufferCapacity(bc);
      if (items.length > 0) {
        lastReceivedAtRef.current = items[0].occurredAt;
        setLastEventAt(new Date(items[0].occurredAt).getTime());
      }
    }
  }, [recentQuery.data]);

  // --- prepend new events to top of list ---
  const prependEvent = useCallback((item: EventStreamItem) => {
    setNewEventIds((prev) => {
      const next = new Set(prev);
      next.add(item.eventId);
      setTimeout(() => {
        setNewEventIds((p) => {
          const n = new Set(p);
          n.delete(item.eventId);
          return n;
        });
      }, 1500);
      return next;
    });
    setEvents((prev) => {
      const next = [item, ...prev].slice(0, MAX_DISPLAY_EVENTS);
      return next;
    });
    lastReceivedAtRef.current = item.occurredAt;
    setLastEventAt(Date.now());
  }, []);

  // --- SSE connection ---
  const connectSSE = useCallback(async () => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    const typeFilter =
      pendingTypes.length === 1 ? pendingTypes[0] : undefined;

    // R1-004: issue short-lived ticket then create EventSource (avoids JWT in URL)
    let es: EventSource;
    try {
      const ticket = await eventMonitorApi.issueTicket();
      const fullUrl = eventMonitorApi.getStreamUrl(ticket, typeFilter);
      es = new EventSource(fullUrl);
    } catch {
      setConnectionMode('polling');
      pollingEnabledRef.current = true;
      return;
    }
    sseRef.current = es;

    es.addEventListener('bizEvent', (e: MessageEvent) => {
      try {
        const item = JSON.parse(e.data as string) as EventStreamItem;
        if (isPaused) {
          pausedQueueRef.current.push(item);
          setPausedCount((n) => n + 1);
        } else {
          prependEvent(item);
          setBufferSize((n) => Math.min(n + 1, bufferCapacity));
        }
      } catch {
        // invalid JSON — ignore
      }
    });

    // R7-083 / R649-FE-002(2차): "connected" 이벤트 — 서버 SSE 스트림 초기화 완료 신호.
    // 재연결 카운터는 여기서 즉시 리셋하지 않는다 — 즉사 재현 시 매 사이클 리셋되어
    // attempt 가 항상 1 에 머무는 무한 루프 결함이 있었다. 연결 시각만 기록해 두고,
    // 실제 리셋 여부는 onerror 에서 생존시간(STABLE_CONNECTION_MS) 기준으로 판단한다.
    es.addEventListener('connected', () => {
      setConnectionMode('live');
      pollingEnabledRef.current = false;
      connectedAtRef.current = Date.now();
      setLastEventAt(Date.now());
    });

    es.addEventListener('ping', () => {
      // keepalive ping — update idle tracker
      setLastEventAt(Date.now());
    });

    // R7-026 / R7-083 / R649-FE-001 / R649-FE-002(2차): onerror — readyState 분기를 두지
    // 않고 항상 자체 백오프 재연결 경로로 넘어간다. 네이티브 EventSource 의 자동 재연결에
    // 맡기면 이미 소비된 1회용 ticket 이 그대로 담긴 URL 로 재시도되어 즉시 401 을 반복하므로,
    // 매 오류마다 우리가 직접 close() 하고 새 ticket 으로 재연결을 스케줄한다.
    es.onerror = (errorEvent) => {
      void errorEvent; // suppress lint unused var
      es.close();
      sseRef.current = null;

      // R649-FE-002(2차): 직전 연결이 STABLE_CONNECTION_MS 이상 유지됐을 때만 카운터 리셋.
      // 서버가 connected 직후 즉사를 반복하는 결함이 있으면 이 조건이 항상 거짓이 되어
      // attempt 가 계속 누적 → 정상적으로 5회 예산이 소진되고 'stopped' 로 전이한다.
      const connectedAt = connectedAtRef.current;
      connectedAtRef.current = null;
      if (connectedAt !== null && Date.now() - connectedAt >= STABLE_CONNECTION_MS) {
        reconnectAttemptRef.current = 0;
      }

      reconnectAttemptRef.current += 1;
      const attempt = reconnectAttemptRef.current;

      if (attempt > SSE_MAX_RECONNECT_ATTEMPTS) {
        setConnectionMode('stopped');
        pollingEnabledRef.current = false;
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        return;
      }

      setConnectionMode('polling');
      pollingEnabledRef.current = true;

      const delay = Math.min(
        SSE_RECONNECT_DELAY_BASE * 2 ** (attempt - 1),
        SSE_RECONNECT_MAX_DELAY
      );
      reconnectTimerRef.current = setTimeout(() => {
        void connectSSE();
      }, delay);
    };

    es.onopen = () => {
      setConnectionMode('live');
      pollingEnabledRef.current = false;
      connectedAtRef.current = Date.now();
    };
  }, [isPaused, prependEvent, pendingTypes, bufferCapacity]);

  // R649-FE-001: 사용자가 [다시 연결] 버튼을 클릭했을 때 — 카운터 리셋 후 재연결
  const handleReconnect = useCallback(() => {
    reconnectAttemptRef.current = 0;
    connectedAtRef.current = null;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setConnectionMode('connecting');
    void connectSSE();
  }, [connectSSE]);

  // Mount: open SSE
  useEffect(() => {
    void connectSSE();
    return () => {
      sseRef.current?.close();
      sseRef.current = null;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- pause toggle ---
  const handlePauseToggle = useCallback(() => {
    if (isPaused) {
      const queued = [...pausedQueueRef.current];
      pausedQueueRef.current = [];
      setPausedCount(0);
      setEvents((prev) => {
        const next = [...queued, ...prev].slice(0, MAX_DISPLAY_EVENTS);
        return next;
      });
      if (queued.length > 0) {
        const ids = new Set(queued.map((e) => e.eventId));
        setNewEventIds((prev) => {
          const n = new Set([...prev, ...ids]);
          setTimeout(() => {
            setNewEventIds((p) => {
              const clean = new Set(p);
              ids.forEach((id) => clean.delete(id));
              return clean;
            });
          }, 1500);
          return n;
        });
      }
      setConnectionMode(sseRef.current ? 'live' : 'polling');
    } else {
      setConnectionMode('paused');
    }
    setIsPaused((p) => !p);
  }, [isPaused]);

  // --- apply filter ---
  const handleApplyFilter = useCallback(() => {
    const condition: RecentEventsCondition = { limit: 100 };
    if (pendingTypes.length > 0) condition.type = pendingTypes.join(',');
    if (pendingSources.length > 0) condition.source = pendingSources.join(',');
    if (pendingUserId.trim()) condition.userId = pendingUserId.trim();
    if (pendingSince.trim()) {
      condition.since = new Date(pendingSince).toISOString();
    }
    setAppliedCondition(condition);
  }, [pendingTypes, pendingSources, pendingUserId, pendingSince]);

  // --- reset filter ---
  const handleResetFilter = useCallback(() => {
    setPendingTypes([]);
    setPendingSources([]);
    setPendingUserId('');
    setPendingSince('');
    setAppliedCondition({ limit: 100 });
  }, []);

  // --- refresh ---
  const handleRefresh = useCallback(() => {
    void recentQuery.refetch();
    void filtersQuery.refetch();
  }, [recentQuery, filtersQuery]);

  // --- buffer clear ---
  const handleClearConfirm = useCallback(() => {
    clearMutation.mutate(undefined, {
      onSuccess: (data) => {
        setEvents([]);
        setBufferSize(0);
        pausedQueueRef.current = [];
        setPausedCount(0);
        setShowClearConfirm(false);
        setToast({
          message: t('eventMonitor.empty.cleared', { count: data.clearedCount }),
          type: 'success',
        });
        setTimeout(() => setToast(null), 3500);
      },
      onError: () => {
        setShowClearConfirm(false);
        setToast({ message: t('eventMonitor.error.clearFailed'), type: 'warning' });
        setTimeout(() => setToast(null), 3500);
      },
    });
  }, [clearMutation, t]);

  // --- row click ---
  const handleRowClick = useCallback((event: EventStreamItem) => {
    setSelectedEvent(event);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  // --- derived state ---
  const isInitialLoading = recentQuery.isLoading;
  const isError = recentQuery.isError && events.length === 0;
  const isEmpty = !isInitialLoading && !isError && events.length === 0;
  const showTable = !isInitialLoading && !isError && events.length > 0;

  const bufferRatio = bufferCapacity > 0 ? bufferSize / bufferCapacity : 0;

  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
    border: '1px solid transparent',
  };

  // Live-mode badge label — use same text regardless of idle, indicator color conveys state
  const liveBadgeLabel = t('eventMonitor.status.live');

  function modeBadgeLabel(mode: ConnectionMode): string {
    if (mode === 'connecting') return t('eventMonitor.status.connecting');
    if (mode === 'live') return liveBadgeLabel;
    if (mode === 'paused') return t('eventMonitor.status.paused');
    if (mode === 'polling') return t('eventMonitor.status.polling');
    if (mode === 'stopped') return t('eventMonitor.status.stopped');
    return t('eventMonitor.status.disconnected');
  }

  return (
    <div style={{ padding: 0 }}>
      {/* Page header */}
      <PageHeader
        title={t('eventMonitor.page.title')}
        breadcrumb={<span>{t('eventMonitor.page.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Pause / Resume */}
            <button
              type="button"
              onClick={handlePauseToggle}
              aria-label={isPaused ? t('eventMonitor.action.resumeAria') : t('eventMonitor.action.pauseAria')}
              aria-pressed={isPaused}
              style={{
                ...btnBase,
                background: isPaused ? 'var(--color-success)' : 'var(--color-warning)',
                color: 'var(--color-text-inverse)',
              }}
            >
              {isPaused ? (
                <>
                  {t('eventMonitor.action.resume')}
                  {pausedCount > 0 && (
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        background: 'rgba(0,0,0,0.2)',
                        padding: '0 var(--spacing-1)',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      +{pausedCount}
                    </span>
                  )}
                </>
              ) : (
                t('eventMonitor.action.pause')
              )}
            </button>

            {/* Buffer clear */}
            <button
              type="button"
              onClick={() => setShowClearConfirm(true)}
              aria-label={t('eventMonitor.action.clearAria')}
              style={{
                ...btnBase,
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                color: 'var(--color-text-primary)',
              }}
            >
              {t('eventMonitor.action.clear')}
            </button>

            {/* Refresh */}
            <button
              type="button"
              onClick={handleRefresh}
              aria-label={t('eventMonitor.action.refreshAria')}
              disabled={isInitialLoading}
              style={{
                ...btnBase,
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                color: 'var(--color-text-primary)',
                opacity: isInitialLoading ? 0.6 : 1,
                cursor: isInitialLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {t('eventMonitor.action.refresh')}
            </button>
          </div>
        }
      />

      {/* Volatile data banner */}
      <div
        role="note"
        aria-label={t('eventMonitor.banner.ariaNote')}
        style={{
          background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
          border: '1px solid var(--color-warning)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
          display: 'flex',
          gap: 'var(--spacing-3)',
          alignItems: 'flex-start',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-primary)',
        }}
      >
        <span aria-hidden="true" style={{ color: 'var(--color-warning)', fontSize: 'var(--font-size-xl)', flexShrink: 0 }}>
          ⚠️
        </span>
        <div>
          <strong style={{ color: 'var(--color-warning)' }}>{t('eventMonitor.banner.volatile')}</strong> —{' '}
          {t('eventMonitor.banner.volatileDesc')}{' '}
          <a
            href="/audit-logs"
            style={{
              color: 'var(--color-brand-primary)',
              textDecoration: 'underline',
            }}
          >
            {t('eventMonitor.banner.volatileLink')}
          </a>
          {t('eventMonitor.banner.volatileDescSuffix')}
        </div>
      </div>

      {/* Main layout — left filter + right content */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '280px 1fr',
          gap: 'var(--spacing-4)',
          alignItems: 'start',
        }}
      >
        {/* Left: filter panel */}
        <EventFilterBar
          selectedTypes={pendingTypes}
          selectedSources={pendingSources}
          userId={pendingUserId}
          since={pendingSince}
          availableTypes={filtersQuery.data?.types ?? []}
          availableSources={filtersQuery.data?.sources ?? []}
          onTypesChange={setPendingTypes}
          onSourcesChange={setPendingSources}
          onUserIdChange={setPendingUserId}
          onSinceChange={setPendingSince}
          onApply={handleApplyFilter}
          onReset={handleResetFilter}
          isLoading={isInitialLoading}
        />

        {/* Right: stream bar + table */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)', minWidth: 0 }}>

          {/* Stream control bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-surface-muted)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              flexWrap: 'wrap',
            }}
          >
            {/* Buffer usage */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)' }}>
              <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                {t('eventMonitor.buffer.label')}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-sm)',
                  color: bufferRatio >= 0.85 ? 'var(--color-danger)' : 'var(--color-text-primary)',
                  fontWeight: 700,
                }}
                aria-label={t('eventMonitor.buffer.ariaUsage', { size: bufferSize, capacity: bufferCapacity })}
              >
                {bufferSize} / {bufferCapacity}
              </span>
              <div
                aria-label={t('eventMonitor.buffer.ariaProgress', { pct: Math.round(bufferRatio * 100) })}
                role="progressbar"
                aria-valuenow={bufferSize}
                aria-valuemin={0}
                aria-valuemax={bufferCapacity}
                style={{
                  width: 160,
                  height: 8,
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(bufferRatio * 100, 100)}%`,
                    borderRadius: 'var(--radius-full)',
                    background: bufferFillColor(bufferSize, bufferCapacity),
                    transition: 'width 200ms ease',
                  }}
                />
              </div>
            </div>

            {/* Connection mode badge */}
            <span
              aria-label={t('eventMonitor.status.ariaLabel', { label: modeBadgeLabel(connectionMode) })}
              title={
                connectionMode === 'live' && isLiveIdle
                  ? t('eventMonitor.status.liveIdleTooltip')
                  : undefined
              }
              data-testid="live-pill"
              data-idle={connectionMode === 'live' && isLiveIdle ? 'true' : 'false'}
              style={modeBadgeStyle(connectionMode, isLiveIdle)}
            >
              {connectionMode === 'live' && (
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'currentColor',
                    flexShrink: 0,
                    animation: isLiveIdle ? 'none' : 'em-pulse 1.4s infinite ease-in-out',
                    opacity: isLiveIdle ? 0.45 : 1,
                    display: 'inline-block',
                  }}
                />
              )}
              {modeBadgeLabel(connectionMode)}
            </span>

            {/* Polling mode indicator */}
            {connectionMode === 'polling' && (
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 10,
                  color: 'var(--color-text-muted)',
                  padding: '1px var(--spacing-2)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                {t('eventMonitor.status.pollInterval')}
              </span>
            )}

            {/* Paused queue counter */}
            {isPaused && pausedCount > 0 && (
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-warning)',
                  background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                  border: '1px solid var(--color-warning)',
                  padding: '1px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                }}
                aria-live="polite"
              >
                {t('eventMonitor.queue.pending', { count: pausedCount })}
              </span>
            )}

            {/* Filtered event count */}
            {!isPaused && (pendingTypes.length > 0 || pendingSources.length > 0) && events.length > 0 && (
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginLeft: 'auto',
                }}
              >
                {t('eventMonitor.table.filteredCount', { count: events.length })}
              </span>
            )}
          </div>

          {/* Stream stopped state — R649: reconnect budget (5 attempts) exhausted */}
          {connectionMode === 'stopped' && (
            <div style={{ padding: 'var(--spacing-2)' }}>
              <ErrorState
                title={t('eventMonitor.error.streamStopped')}
                message={t('eventMonitor.error.streamStopped')}
                actions={[
                  {
                    label: t('eventMonitor.button.reconnect'),
                    onClick: handleReconnect,
                    variant: 'primary',
                  },
                ]}
              />
            </div>
          )}

          {/* Error state */}
          {isError && (
            <div style={{ padding: 'var(--spacing-2)' }}>
              <ErrorState
                title={t('eventMonitor.error.loadFailed')}
                message={
                  (recentQuery.error as unknown as ApiError)?.code === 'EVENT-403-001' ||
                  (recentQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                    ? t('eventMonitor.error.forbidden')
                    : ((recentQuery.error as unknown as ApiError)?.message ??
                      t('eventMonitor.error.networkError'))
                }
                onRetry={() => void recentQuery.refetch()}
              />
            </div>
          )}

          {/* Empty state */}
          {isEmpty && (
            <EmptyState
              title={t('eventMonitor.empty.title')}
              description={t('eventMonitor.empty.desc')}
              action={
                <button
                  type="button"
                  onClick={handleResetFilter}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('eventMonitor.empty.resetFilter')}
                </button>
              }
            />
          )}

          {/* Event table (default + loading skeleton) */}
          {(isInitialLoading || showTable) && (
            <EventTable
              events={events}
              newEventIds={newEventIds}
              selectedEventId={selectedEvent?.eventId ?? null}
              isLoading={isInitialLoading}
              isPaused={isPaused}
              onRowClick={handleRowClick}
            />
          )}

          {/* Event count */}
          {showTable && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                textAlign: 'right',
              }}
              aria-live="polite"
            >
              {t('eventMonitor.table.totalCount', { count: events.length, max: MAX_DISPLAY_EVENTS })}
            </div>
          )}
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'var(--color-surface-elevated)',
            border: `1px solid ${toast.type === 'success' ? 'var(--color-success)' : 'var(--color-warning)'}`,
            borderLeft: `4px solid ${toast.type === 'success' ? 'var(--color-success)' : 'var(--color-warning)'}`,
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            maxWidth: 420,
            zIndex: 400,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
          }}
        >
          <span aria-hidden="true">
            {toast.type === 'success' ? '✅' : '⚠️'}
          </span>
          {toast.message}
        </div>
      )}

      {/* EventDetailDrawer — dialog state */}
      {selectedEvent && (
        <EventDetailDrawer event={selectedEvent} onClose={handleDrawerClose} />
      )}

      {/* ClearBufferConfirm — dialog state */}
      {showClearConfirm && (
        <ClearBufferConfirm
          bufferSize={bufferSize}
          onConfirm={handleClearConfirm}
          onCancel={() => setShowClearConfirm(false)}
          isLoading={clearMutation.isPending}
        />
      )}

      <style>{`
        @keyframes em-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.8); }
        }
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 280px"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
